import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { isLifeDisabilityType, isSupplementalType } from "@shared/schemas/plans";
import { isFsaPlanType } from "@shared/schemas/planTypes";
import { useFsaConfigs } from "@/hooks/useFsaConfigs";
import type { MedicalBenefits } from "./benefitDetailFormat";

// One election to resolve benefit details for.
export interface BenefitDetailElection {
  planId: number;
  type: string;
}

export type BenefitDetailKind =
  | "medical"
  | "lifeDisability"
  | "supplemental"
  | "fsa";

export type BenefitDetailStatus = "loading" | "empty" | "ready";

export interface BenefitDetailData {
  kind: BenefitDetailKind;
  status: BenefitDetailStatus;
  // Payload shape depends on kind:
  //  - medical → { categories: MedicalBenefits, raw: RawBenefitLine[] }
  //  - lifeDisability → { config, ageReductions, raw }
  //  - supplemental → { config: supplementalPlanConfig row, raw }
  //  - fsa → fsaPlanConfig row (or null; the employee's annualElection is
  //    passed separately at the render layer)
  payload: any;
}

function kindForType(type: string): BenefitDetailKind {
  if (isFsaPlanType(type)) return "fsa";
  if (isLifeDisabilityType(type)) return "lifeDisability";
  if (isSupplementalType(type)) return "supplemental";
  return "medical";
}

// Internal: a flat react-query descriptor tagged so results map back to a plan.
interface QueryTag {
  planId: number;
  slot: "benefits" | "rawBenefits" | "ldConfig" | "ldAge" | "supplemental";
}

/**
 * Resolves per-type benefit detail for a set of elections in a single hook
 * call. Reuses existing query keys so the react-query cache is shared with
 * PlanComparisonDialog (["plan-benefits", id]) and ActiveBenefitRow
 * (["/api/plans", id, "life-disability-config"]); FSA is served from the bulk
 * useFsaConfigs hook so no per-plan fetch is added.
 */
export function useBenefitDetailData(
  elections: BenefitDetailElection[],
): Map<number, BenefitDetailData> {
  // Stable, deduped list — elections can repeat planId across renders.
  const resolved = useMemo(
    () => elections.map((election) => ({ ...election, kind: kindForType(election.type) })),
    [elections],
  );

  const { fsaConfigByPlanId, isFetched: fsaFetched } = useFsaConfigs();

  // Build the flat query list with tags to reassemble afterward. Tags must be
  // memoized alongside configs — they're consumed in the assembly memo below
  // and would otherwise be empty on cached renders.
  const { queryConfigs, tags } = useMemo(() => {
    const configs: any[] = [];
    const builtTags: QueryTag[] = [];
    resolved.forEach(({ planId, kind }) => {
      // The plan's raw benefit lines — the Plan Builder's editable source
      // (per-type field templates). Fetched for every templated kind so the
      // SBC-style summary shows alongside structured config blocks too.
      const pushRawBenefitsQuery = () => {
        builtTags.push({ planId, slot: "rawBenefits" });
        configs.push({
          queryKey: ["plan-raw-benefits", planId],
          enabled: !!planId,
          staleTime: 5 * 60 * 1000,
          queryFn: async () => {
            const response = await fetch(`/api/plans/${planId}`, {
              credentials: "include",
            });
            if (!response.ok) return [];
            const plan = await response.json();
            return Array.isArray(plan?.rawBenefits) ? plan.rawBenefits : [];
          },
        });
      };
      if (kind === "medical") {
        builtTags.push({ planId, slot: "benefits" });
        configs.push({
          queryKey: ["plan-benefits", planId],
          enabled: !!planId,
          staleTime: 5 * 60 * 1000,
          queryFn: async (): Promise<MedicalBenefits> => {
            const response = await fetch(`/api/plan-benefits/${planId}`, {
              credentials: "include",
            });
            if (!response.ok) return {};
            return (await response.json()) as MedicalBenefits;
          },
        });
        // When present, raw lines take display precedence over the parsed
        // categories so portals match what the library shows (WbBenefitsBlock).
        pushRawBenefitsQuery();
      } else if (kind === "lifeDisability") {
        pushRawBenefitsQuery();
        builtTags.push({ planId, slot: "ldConfig" });
        configs.push({
          queryKey: ["/api/plans", planId, "life-disability-config"],
          enabled: !!planId,
          staleTime: Infinity,
          queryFn: async () => {
            const response = await fetch(`/api/plans/${planId}/life-disability-config`);
            if (!response.ok) return null;
            return response.json();
          },
        });
        builtTags.push({ planId, slot: "ldAge" });
        configs.push({
          queryKey: ["/api/plans", planId, "age-reduction-schedules"],
          enabled: !!planId,
          staleTime: Infinity,
          queryFn: async () => {
            const response = await fetch(`/api/plans/${planId}/age-reduction-schedules`);
            if (!response.ok) return [];
            const data = await response.json();
            return Array.isArray(data) ? data : [];
          },
        });
      } else if (kind === "supplemental") {
        pushRawBenefitsQuery();
        builtTags.push({ planId, slot: "supplemental" });
        configs.push({
          queryKey: ["/api/plans", planId, "supplemental-config"],
          enabled: !!planId,
          staleTime: Infinity,
          queryFn: async () => {
            const response = await fetch(`/api/plans/${planId}/supplemental-config`);
            if (!response.ok) return null;
            return response.json();
          },
        });
      }
      // fsa: no per-plan query — resolved from useFsaConfigs below.
    });
    return { queryConfigs: configs, tags: builtTags };
  }, [resolved]);

  const queryResults = useQueries({ queries: queryConfigs });

  return useMemo(() => {
    // Gather query results back per planId.
    const benefitsByPlanId = new Map<number, { data: any; isLoading: boolean }>();
    const rawBenefitsByPlanId = new Map<number, { data: any; isLoading: boolean }>();
    const ldConfigByPlanId = new Map<number, { data: any; isLoading: boolean }>();
    const ldAgeByPlanId = new Map<number, { data: any; isLoading: boolean }>();
    const supplementalByPlanId = new Map<number, { data: any; isLoading: boolean }>();

    tags.forEach((tag, index) => {
      const result = queryResults[index];
      const entry = { data: result?.data, isLoading: !!result?.isLoading };
      if (tag.slot === "benefits") benefitsByPlanId.set(tag.planId, entry);
      else if (tag.slot === "rawBenefits") rawBenefitsByPlanId.set(tag.planId, entry);
      else if (tag.slot === "ldConfig") ldConfigByPlanId.set(tag.planId, entry);
      else if (tag.slot === "ldAge") ldAgeByPlanId.set(tag.planId, entry);
      else if (tag.slot === "supplemental") supplementalByPlanId.set(tag.planId, entry);
    });

    const output = new Map<number, BenefitDetailData>();

    resolved.forEach(({ planId, kind }) => {
      if (kind === "medical") {
        const entry = benefitsByPlanId.get(planId);
        const rawEntry = rawBenefitsByPlanId.get(planId);
        const benefits = (entry?.data || {}) as MedicalBenefits;
        const raw = Array.isArray(rawEntry?.data) ? rawEntry!.data : [];
        const hasAny = Object.keys(benefits).length > 0 || raw.length > 0;
        const isLoading = !!entry?.isLoading || !!rawEntry?.isLoading;
        output.set(planId, {
          kind,
          status: isLoading ? "loading" : hasAny ? "ready" : "empty",
          // categories = parsed plan_benefits rows (legacy); raw = the plan's
          // W&B lines as edited in the Plan Builder (display precedence).
          payload: { categories: benefits, raw },
        });
      } else if (kind === "lifeDisability") {
        const configEntry = ldConfigByPlanId.get(planId);
        const ageEntry = ldAgeByPlanId.get(planId);
        const rawEntry = rawBenefitsByPlanId.get(planId);
        const raw = Array.isArray(rawEntry?.data) ? rawEntry!.data : [];
        const isLoading =
          !!configEntry?.isLoading || !!ageEntry?.isLoading || !!rawEntry?.isLoading;
        const config = configEntry?.data ?? null;
        output.set(planId, {
          kind,
          status: isLoading ? "loading" : config || raw.length > 0 ? "ready" : "empty",
          payload: { config, ageReductions: ageEntry?.data ?? [], raw },
        });
      } else if (kind === "supplemental") {
        const entry = supplementalByPlanId.get(planId);
        const rawEntry = rawBenefitsByPlanId.get(planId);
        const raw = Array.isArray(rawEntry?.data) ? rawEntry!.data : [];
        const config = entry?.data ?? null;
        output.set(planId, {
          kind,
          status:
            entry?.isLoading || rawEntry?.isLoading
              ? "loading"
              : config || raw.length > 0
                ? "ready"
                : "empty",
          // config = structured supplemental row; raw = template benefit lines.
          payload: { config, raw },
        });
      } else {
        // fsa
        const config = fsaConfigByPlanId.get(planId) ?? null;
        // FSA always has the employee election to show, so it's never purely
        // "empty"; treat a present config as ready, absent config as ready too
        // (the block renders the election alone). Only show loading until the
        // bulk fetch settles.
        output.set(planId, {
          kind,
          status: !fsaFetched ? "loading" : "ready",
          payload: config,
        });
      }
    });

    return output;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved, queryResults, fsaConfigByPlanId, fsaFetched]);
}
